import { Text } from '@modusign/design-system';
import { ReactNode } from 'react';

import { CONTACT_INFO_MAX_LENGTH } from '@app/constants/contactInfoMaxLength';
import validateName, { MIN_LENGTH_ERROR } from '@app/features/addressBook/libs/validateName';
import { AddressBookExcelFormColumnType } from '@app/libs/excel/addressBook/AddressBookExcelForm';
import { removeNotNumberFromString } from '@app/libs/inputFormat';
import { validateEmail } from '@app/libs/validation';

export const NameValidationErrorMessage = (
  <>
    <Text color="textEmphasisBase" fontSize="sm">
      30자 이하
    </Text>
    로 입력해 주세요. (이모지 제외)
    <br />
    특수문자는{' '}
    <Text color="textEmphasisBase" fontSize="sm">
      {'~ ! @ # $ % ^ & - _ ( ) { } [ ] . '}
    </Text>
    만 사용할 수 있습니다.
  </>
);

export const BizNumberValidationError = (
  <>
    - 를 제외한{' '}
    <Text color="textEmphasisBase" fontSize="sm">
      10자리 숫자
    </Text>
    만 입력해 주세요.
  </>
);

export type ValidateColumnValueParams = {
  columnIndex: number;
  rowIndex: number;
  type: AddressBookExcelFormColumnType;
  value: string;
};

export type ValidateColumnValueReturns = {
  column: number;
  message: ReactNode;
  row: number;
  type: AddressBookExcelFormColumnType;
} | null;

export default function validateColumnValue({
  columnIndex,
  rowIndex,
  type,
  value,
}: ValidateColumnValueParams): ValidateColumnValueReturns {
  if (value === 'undefined') {
    return {
      column: columnIndex,
      message:
        '데이터를 읽을 수 없습니다. 엑셀양식을 다시 확인해 주세요. 사용자 서식을 이용 시 문제가 발생할 수 있습니다.',
      row: rowIndex,
      type,
    };
  }

  if (type === 'NAME') {
    const message = validateName(value);
    if (message !== true) {
      return {
        column: columnIndex,
        message: value ? NameValidationErrorMessage : '연락처 이름은 필수입니다.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'DEFAULT_FORWARDING_METHOD') {
    if (value !== 'KAKAO' && value !== 'EMAIL') {
      return {
        column: columnIndex,
        message: '기본 전송 수단 선택은 필수입니다.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'PHONE_NUMBER' && !!value) {
    if (removeNotNumberFromString(value).length !== 11 || !value.startsWith('010')) {
      return {
        column: columnIndex,
        message: '010으로 시작하는 11자리 숫자만 입력',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'EMAIL' && !!value) {
    if (value.length > CONTACT_INFO_MAX_LENGTH) {
      return {
        column: columnIndex,
        message: `연락처는 ${CONTACT_INFO_MAX_LENGTH}자 이내로 입력해 주세요.`,
        row: rowIndex,
        type,
      };
    }

    if (!validateEmail(value).success) {
      return {
        column: columnIndex,
        message: '올바른 이메일 주소가 아닙니다.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'DESCRIPTION') {
    if (value.length > 100) {
      return {
        column: columnIndex,
        message: (
          <>
            <Text color="textEmphasisBase" fontSize="sm">
              100자 이하
            </Text>
            로 입력해 주세요.
          </>
        ),
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'COMPANY_NAME' && !!value) {
    const message = validateName(value);
    if (message !== true) {
      return {
        column: columnIndex,
        message: NameValidationErrorMessage,
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'COMPANY_POSITION' && !!value) {
    const message = validateName(value);
    if (message !== true && message !== MIN_LENGTH_ERROR) {
      return {
        column: columnIndex,
        message: NameValidationErrorMessage,
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'BIZ_NUMBER' && !!value) {
    if (removeNotNumberFromString(value).length !== 10) {
      return {
        column: columnIndex,
        message: BizNumberValidationError,
        row: rowIndex,
        type,
      };
    }
  }

  return null;
}
